// leads.js — v1.0.0 (Lead Management)
//
// Thin data-access layer for the leads API. The leads table is fronted by
// 4 Netlify functions (create-lead / get-leads / update-lead / delete-lead)
// rather than direct supabase-js calls — this is the same JWT-Bearer
// pattern AuthContext.js uses for the MFA endpoints. Server-side enforces
// user_id from the verified token, so a leaked anon key + service-role
// function combination can't write rows on someone else's behalf.
//
// All four helpers return the same { data, error } shape the rest of the
// codebase uses, so callers can treat them uniformly.
import { supabase } from './supabaseClient';
import { API_BASE_URL } from '../services/api';

const NOT_SIGNED_IN = {
  data: null,
  error: { message: 'Not signed in. Please sign in and try again.' },
};

const NOT_CONFIGURED = {
  data: null,
  error: {
    message:
      'Supabase is not configured. Fill in frontend/.env (REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY) and restart the dev server.',
  },
};

// Mirrors the helper in AuthContext.js: pull the live access_token, call
// a Netlify function with the Bearer header, and normalize the response
// into { data, error }. Kept local instead of imported so leads.js has no
// hidden dependencies on auth internals.
async function callApi(path, init = {}) {
  if (!supabase) return NOT_CONFIGURED;

  const { data: sessionData } = await supabase.auth.getSession();
  const accessToken = sessionData?.session?.access_token;
  if (!accessToken) return NOT_SIGNED_IN;

  try {
    const response = await fetch(`${API_BASE_URL}${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
        ...(init.headers || {}),
      },
    });
    const payload = await response.json().catch(() => null);
    if (!response.ok || payload?.success === false) {
      return {
        data: null,
        error: {
          message: payload?.error || `Request failed (HTTP ${response.status}).`,
          status: response.status,
          payload,
        },
      };
    }
    return { data: payload, error: null };
  } catch (err) {
    return { data: null, error: { message: err?.message || 'Network error.' } };
  }
}

/**
 * Create a new lead.
 * @param {object} input
 *   - entry_type   (required, one of fsbo/parcel/lease_leverage/off_market/other)
 *   - address, city, state, zip, lat, lng, asking_price, notes (all optional)
 * Returns { data: { lead }, error }.
 */
export async function createLead(input) {
  return callApi('/create-lead', {
    method: 'POST',
    body: JSON.stringify(input || {}),
  });
}

/**
 * List the current user's leads, newest first.
 * Returns { data: { leads: [...] }, error }.
 */
export async function listLeads() {
  return callApi('/get-leads', { method: 'GET' });
}

/**
 * PATCH a lead the user owns. Any combination of status / notes /
 * asking_price / lead_name / current_monthly_revenue can be passed;
 * everything else is ignored server-side.
 * Returns { data: { lead }, error }.
 */
export async function updateLead({
  id,
  status,
  notes,
  asking_price,
  lead_name,
  current_monthly_revenue,
}) {
  const body = { id };
  if (status !== undefined) body.status = status;
  if (notes !== undefined) body.notes = notes;
  if (asking_price !== undefined) body.asking_price = asking_price;
  if (lead_name !== undefined) body.lead_name = lead_name;
  if (current_monthly_revenue !== undefined) {
    body.current_monthly_revenue = current_monthly_revenue;
  }
  return callApi('/update-lead', {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

/**
 * Delete a lead the user owns.
 * Returns { data: { success: true }, error }.
 */
export async function deleteLead(id) {
  return callApi('/delete-lead', {
    method: 'DELETE',
    body: JSON.stringify({ id }),
  });
}
